import { Card } from '@consta/uikit/Card';
import { Text } from '@consta/uikit/Text';
import { Button } from '@consta/uikit/Button';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import classes from './style/ProjectChromatogram.module.scss';
import { Project } from '../../../../../services/types/TProjectServices';
import { useLazyGetChromatogramByProjectQuery } from '../../../../../services/chromatogramService';
import AddChroma from '../../../../Modals/AddChroma/AddChroma';
import { useLazyGetReportWellInfoQuery } from '../../../../../services/wellService';

interface ProjectChromatogramProps {
    projectData?: Project;
    onUpdateChromatogram: () => void;
}

const ProjectChromatogram = ({ projectData, onUpdateChromatogram }: ProjectChromatogramProps) => {
    const { projectUuid } = useParams<{ projectUuid: string }>();
    const [triggerGetChromatogram, { data: chromatogramData, isLoading, error }] = useLazyGetChromatogramByProjectQuery();
    const [isAddChromaModalOpen, setIsAddChromaModalOpen] = useState(false);
    const [imageLoadError, setImageLoadError] = useState(false);
    const [getReportWellInfo, {data: wellInfo}] = useLazyGetReportWellInfoQuery()

    // Загружаем хроматограмму при монтировании компонента
    useEffect(() => {
        if (projectUuid) {
            //('🔄 Загрузка хроматограммы для проекта:', projectUuid);
            triggerGetChromatogram({ project_uuid: projectUuid });
        }
    }, [projectUuid, triggerGetChromatogram]);
    //(chromatogramData)

    useEffect(() => {
                if (chromatogramData) {
                getReportWellInfo({ uuid: chromatogramData?.[0]?.well });
            }
        },[chromatogramData])

    // Сбрасываем ошибку загрузки изображения при смене данных
    useEffect(() => {
        setImageLoadError(false);
    }, [chromatogramData]);

    // Получаем данные хроматограммы из chromatogramData
    const chromatogram = chromatogramData && Array.isArray(chromatogramData) && chromatogramData.length > 0
        ? chromatogramData[0]
        : null;

    const currentImage: any = chromatogram?.image && chromatogram.image.trim() !== ''
        ? chromatogram.image
        : null;

    const chromatogramName = chromatogram?.name
        ? chromatogram.name
        : (chromatogramData && Array.isArray(chromatogramData) && chromatogramData.length > 0
            ? 'Хроматограмма проекта'
            : 'Хроматограмма не найдена');

    //('🎯 Текущая хроматограмма:', chromatogram);
    //('🖼️ Текущее изображение:', currentImage);
    //('📛 Текущее название:', chromatogramName);

    const handleUpdateChromatogram = () => {
        //('🎯 Открытие модалки для проекта с UUID:', projectUuid);
        //('🎯 Существующая хроматограмма:', chromatogram?.uuid);
        setIsAddChromaModalOpen(true);
    };

    const handleCloseAddChromaModal = () => {
        setIsAddChromaModalOpen(false);
    };

    const handleChromatogramUpdated = () => {
        if (projectUuid) {
            //('🔄 Перезагрузка хроматограммы после обновления');
            triggerGetChromatogram({ project_uuid: projectUuid });
        }
    };

    const handleImageError = () => {
        console.error('❌ Ошибка загрузки изображения:', currentImage);
        setImageLoadError(true);
    };

    const handleImageLoad = () => {
        //('✅ Изображение хроматограммы успешно загружено:', currentImage);
        setImageLoadError(false);
    };

    // Определяем текст кнопки в зависимости от наличия хроматограммы
    const getButtonLabel = () => {
        if (isLoading) return 'Загрузка...';
        if (chromatogram?.uuid) return 'Обновить изображение хроматограммы';
        return 'Создать хроматограмму';
    };

    // Определяем статус хроматограммы для отображения
    const getStatusText = () => {
        if (isLoading) return 'Загрузка...';
        if (error) return 'Ошибка загрузки хроматограммы';
        if (chromatogram?.uuid && currentImage && !imageLoadError) return chromatogramName;
        if (chromatogram?.uuid && (!currentImage || imageLoadError)) return 'Хроматограмма создана, но изображение недоступно';
        return 'Хроматограмма не создана';
    };

    // Рендер состояния загрузки
    const renderLoadingState = () => (
        <div className={classes.loadingState}>
            <Text>Загрузка хроматограммы...</Text>
        </div>
    );

    // Рендер состояния ошибки
    const renderErrorState = () => (
        <div className={classes.errorState}>
            <Text view="warning" className={classes.errorText}>Ошибка загрузки хроматограммы</Text>
            <Text size="s" view="secondary">
                Не удалось загрузить хроматограмму для проекта
            </Text>
            <Button
                size="s"
                label="Попробовать снова"
                onClick={() => projectUuid && triggerGetChromatogram({ project_uuid: projectUuid })}
                className={classes.retryButton}
            />
        </div>
    );

    // Рендер изображения хроматограммы
    const renderChromatogramImage = () => (
        <div className={classes.imageContainer}>
            <img
                src={currentImage}
                alt={`Хроматограмма проекта ${projectUuid}`}
                className={classes.chromatogramImage}
                onError={handleImageError}
                onLoad={handleImageLoad}
                crossOrigin="anonymous"
            />
            {chromatogram?.comment && (
                <Text size="s" view="secondary" className={classes.imageComment}>
                    {chromatogram.comment}
                </Text>
            )}
        </div>
    );

    // Рендер состояния без изображения
    const renderNoImageState = () => (
        <div className={classes.emptyChromatogram}>
            {chromatogram?.uuid ? (
                // Если хроматограмма есть в системе, но нет изображения
                <>
                    <Text view="secondary">Хроматограмма создана в системе</Text>
                    <Text size="s" view="secondary" className={classes.emptyText}>
                        {imageLoadError ? 'Ошибка загрузки изображения' : 'Изображение хроматограммы не загружено'}
                    </Text>
                    {chromatogram?.comment && (
                        <Text size="s" view="secondary" className={classes.emptyText}>
                            Комментарий: {chromatogram.comment}
                        </Text>
                    )}
                    <Text size="xs" view="secondary">
                        Нажмите "Обновить изображение хроматограммы" чтобы загрузить изображение
                    </Text>
                </>
            ) : (
                // Если хроматограммы нет вообще
                <>
                    <Text view="secondary">Хроматограмма не создана</Text>
                    <Text size="s" view="secondary" className={classes.emptyText}>
                        Для этого проекта еще не создана хроматограмма.
                        Нажмите "Создать хроматограмму" чтобы добавить изображение хроматограммы.
                    </Text>
                    <div className={classes.tipsContainer}>
                        <Text size="xs" view="secondary" className={classes.tipText}>
                            Поддерживаемые форматы: JPG, PNG, GIF, BMP
                        </Text>
                        <Text size="xs" view="secondary" className={classes.tipText}>
                            Рекомендуется использовать изображения высокого разрешения
                        </Text>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div className={classes.main}>
            <div className={classes.headerChroma}>
                <div className={classes.headerText}>
                    <Text size='2xl' weight='bold'>
                        Хроматограмма проекта {projectData?.number ? `№ ${projectData.number}` : projectUuid}
                    </Text>
                    <Text size='s' view='secondary' className={classes.statusText}>
                        {getStatusText()}, Месторождение: {wellInfo?.field_name}
                    </Text>
                </div>
                <Button
                    label={getButtonLabel()}
                    onClick={handleUpdateChromatogram}
                    loading={isLoading}
                />
            </div>

            <div className={classes.container}>
                <Card className={classes.cardChroma}>
                    {isLoading ? (
                        renderLoadingState()
                    ) : error ? (
                        renderErrorState()
                    ) : currentImage && !imageLoadError ? (
                        renderChromatogramImage()
                    ) : (
                        renderNoImageState()
                    )}
                </Card>
            </div>

            <AddChroma
                isOpen={isAddChromaModalOpen}
                setIsOpen={setIsAddChromaModalOpen}
                handleCloseModal={handleCloseAddChromaModal}
                projectUuid={projectUuid}
                chromatogramUuid={chromatogram?.uuid}
                onChromatogramUpdated={handleChromatogramUpdated}
                isEditMode={!!chromatogram?.uuid}
            />
        </div>
    );
};

export default ProjectChromatogram;